import { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLanguage, LANGUAGE_OPTIONS } from "../i18n/LanguageContext";

const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);

  const current = LANGUAGE_OPTIONS.find((l) => l.code === lang);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="language-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full border border-gold/25 bg-white/[0.03] px-3.5 py-2 text-xs font-semibold uppercase tracking-widest text-slate-200 transition-colors hover:border-gold/60 hover:text-gold"
      >
        <Globe className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
        <span>{current?.label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute end-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-xl border border-gold/20 bg-[#0c0c10]/95 p-1.5 shadow-2xl shadow-black/60 backdrop-blur-xl"
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <li key={opt.code}>
              <button
                type="button"
                role="option"
                aria-selected={lang === opt.code}
                data-testid={`language-option-${opt.code}`}
                onClick={() => {
                  setLang(opt.code);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  lang === opt.code ? "bg-gold/10 text-gold" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span>{opt.name}</span>
                {lang === opt.code && <Check className="h-4 w-4" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
